import css from './twoListSelect.module.css'
import { ReactNode, useMemo, useState } from 'react'
import { ReactComponent as ArrowRight } from '../../assets/icons/longArrowRight.svg'
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow.svg'
import { ReactComponent as PlusIcon } from '../../assets/icons/icon-plus.svg'
import twoListSelectUtils from './twoListSelect.utils'
import { Virtuoso } from 'react-virtuoso'
import { IOption } from '../../types/global'
import searchUtils, { SearchResult } from '../../utils/search'
import useDebounce from '../hooks/useDebounce'
import Checkbox from '../Inputs/Checkbox'
import ArrowButton from '../Buttons/ArrowButton'
import SearchInput from '../Inputs/SearchInput'
import Button from 'shared/Buttons/Button/Button'
import IconButton from 'shared/Buttons/IconButton'

interface IProps<T extends string | number> {
  options: IOption<T>[]
  optionTitle?: (option: ListSelectOption<T>) => ReactNode
  selected: T[]

  titleLeft: string | JSX.Element
  titleRight: string | JSX.Element

  onSelect?: (values: T[]) => void
  onUnselect?: (value: T[]) => void

  /**
   * Вызывается при изменении порядка в списке selected.
   * Принимает массив значений selected в новом порядке.
   */
  onChangeOrder?: (orderedValues: T[]) => void

  name?: string
  disabled?: boolean

  /**
   * Клик по кнопке подробнее.
   * если передано, показывается кнопка "подробнее" у каждого элемента в списке
   */
  onClickMoreDetails?: (value: T) => void

  onClickAdd?: () => void
}

export interface ListSelectOption<T extends string | number> extends IOption<T> {
  search: SearchResult[]
  index: number
}

const TwoListSelect = <T extends string | number>(props: IProps<T>) => {
  const [leftSelected, setLeftSelected] = useState<Array<T>>([])
  const leftSelectedSet = new Set(leftSelected)
  const [rightSelected, setRightSelected] = useState<Array<T>>([])
  const rightSelectedSet = new Set(rightSelected)

  const [leftSearch, setLeftSearch] = useState('')
  const [rightSearch, setRightSearch] = useState('')
  const debouncedLeftSearch = useDebounce(leftSearch, 300)
  const debouncedRightSearch = useDebounce(rightSearch, 300)
  const selectedSet = new Set(props.selected)

  const leftColSearchResults = useMemo(() => {
    const selectedOptionsInOrder = props.selected
      .map((v) => props.options.find((o) => o.value === v))
      .filter(Boolean) as IOption<T>[]
    return twoListSelectUtils.search(selectedOptionsInOrder, debouncedLeftSearch)
  }, [debouncedLeftSearch, props.options, props.selected])

  const rightColSearchResults = useMemo(() => {
    const notSelectedOptions = props.options.filter((o) => !selectedSet.has(o.value))
    return twoListSelectUtils.search(notSelectedOptions, debouncedRightSearch)
  }, [debouncedRightSearch, props.options, props.selected])

  const handleCheck = (type: 'selected' | 'not-selected', option: ListSelectOption<T>) => (selected: boolean) => {
    if (type === 'not-selected') {
      if (selected) {
        setRightSelected((prev) => [...prev, option.value])
      } else {
        setRightSelected((prev) => prev.filter((v) => v !== option.value))
      }
      setLeftSelected([])
    } else {
      if (selected) {
        setLeftSelected((prev) => [...prev, option.value])
      } else {
        setLeftSelected((prev) => prev.filter((v) => v !== option.value))
      }
      setRightSelected([])
    }
  }

  const handleClick = (type: 'selected' | 'not-selected', option: ListSelectOption<T>) => {
    if (type === 'not-selected') props.onSelect([option.value])
    else props.onUnselect([option.value])
  }

  const handleClickMoreDetails = (value: T) => (e: React.MouseEvent) => {
    e.stopPropagation()

    props.onClickMoreDetails?.(value)
  }

  const handleMoveOrder = (value: T, direction: 'up' | 'down') => (e: React.MouseEvent) => {
    e.stopPropagation()
    if (props.disabled || !props.onChangeOrder) return

    const idx = props.selected.indexOf(value)
    if (idx === -1) return
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === props.selected.length - 1) return

    const newOrder = [...props.selected]
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    ;[newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]]
    props.onChangeOrder(newOrder)
  }

  const renderOrderButtons = (option: ListSelectOption<T>) => {
    if (!props.onChangeOrder || props.disabled) return null

    const idx = props.selected.indexOf(option.value)
    const canMoveUp = idx > 0
    const canMoveDown = idx < props.selected.length - 1

    return (
      <div className={css.orderButtons} onClick={(e) => e.stopPropagation()}>
        <IconButton
          type='button'
          theme='grey-outline'
          disabled={!canMoveUp}
          onClick={handleMoveOrder(option.value, 'up')}
          aria-label='Вверх'
        >
          <ArrowIcon className={css.orderArrowUp} />
        </IconButton>
        <IconButton
          type='button'
          theme='grey-outline'
          disabled={!canMoveDown}
          onClick={handleMoveOrder(option.value, 'down')}
          aria-label='Вниз'
        >
          <ArrowIcon className={css.orderArrowDown} />
        </IconButton>
      </div>
    )
  }

  const renderOption = (column: 'selected' | 'not-selected', option: ListSelectOption<T>) => {
    return (
      <div className={css.row} key={option.value}>
        <div className={css.checkbox}>
          <Checkbox
            disabled={props.disabled}
            onCheck={handleCheck(column, option)}
            checked={column === 'selected' ? leftSelectedSet.has(option.value) : rightSelectedSet.has(option.value)}
          />
        </div>
        <div className={css.rowContent} onClick={() => handleClick(column, option)}>
          {props.optionTitle?.(option) ?? <span>{searchUtils.renderSearchResult(option.search)}</span>}

          <div className={css.rowActions}>
            {column === 'selected' && props.onChangeOrder && (
              <div className={css.orderButtonsWrap}>{renderOrderButtons(option)}</div>
            )}
            {props.onClickMoreDetails ? (
              <div onClick={handleClickMoreDetails(option.value)}>
                <span className='text--body-s text--secondary'>Подробнее</span>
              </div>
            ) : (
              !props.disabled && (
                <div className={css.arrowRight} data-column={column}>
                  <ArrowRight />
                </div>
              )
            )}
          </div>
        </div>
      </div>
    )
  }

  const handleMoveToUnselected = () => {
    if (props.disabled) return

    props.onUnselect(leftSelected)
    setLeftSelected([])
  }

  const handleMoveToSelected = () => {
    if (props.disabled) return

    props.onSelect(rightSelected)
    setRightSelected([])
  }

  const renderSelectedOptions = () => {
    return leftColSearchResults.map((o) => renderOption('selected', o))
  }

  return (
    <div className={css.wrapper}>
      <div className={css.colWrapper}>
        <span className='text--body-m text--secondary text-700' style={{ marginBottom: '1em' }}>
          {props.titleLeft}
        </span>
        <SearchInput
          value={leftSearch}
          name='leftSearch'
          onChange={setLeftSearch}
          placeholder='Поиск...'
          required={false}
          disabled={props.disabled}
        />
        <div className={css.col}>{renderSelectedOptions()}</div>
      </div>
      <div className={css.transferCol}>
        <ArrowButton direction='left' onClick={handleMoveToSelected} />
        <ArrowButton direction='right' onClick={handleMoveToUnselected} />
      </div>
      <div className={css.colWrapper}>
        <span className='text--body-m text--secondary text-700' style={{ marginBottom: '1em' }}>
          {props.titleRight}
        </span>
        <div className='inline-flex'>
          <SearchInput
            value={rightSearch}
            name='rightSearch'
            onChange={setRightSearch}
            placeholder='Поиск...'
            required={false}
            disabled={props.disabled}
          />
          {props.onClickAdd && (
            <Button onClick={props.onClickAdd} theme='grey-outline' className={css.addButton}>
              <PlusIcon />
            </Button>
          )}
        </div>
        <Virtuoso
          className={css.col}
          data={rightColSearchResults}
          itemContent={(index) => renderOption('not-selected', rightColSearchResults[index])}
          overscan={25}
        />
      </div>
    </div>
  )
}

export default TwoListSelect
